import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { KeyRound, Eye, EyeOff, AlertCircle, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';
import './Login.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function PasswordReset() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Request form (no token)
  const [email, setEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form (with token)
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/community/password-reset/request`, { email });
      setRequestSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Senden');
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }
    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/community/password-reset/confirm`, { token, password });
      setResetDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Zurücksetzen');
    }
    setLoading(false);
  };

  // Token provided - show reset form
  if (token) {
    if (resetDone) {
      return (
        <div className="login-page" data-testid="reset-done">
          <div className="login-container">
            <div className="login-card success-card">
              <CheckCircle size={48} className="success-icon-wrap" style={{ color: '#C8A45C' }} />
              <h1 style={{ color: '#C8A45C' }}>Passwort geändert!</h1>
              <p className="success-text">Ihr Passwort wurde erfolgreich zurückgesetzt. Sie koennen sich jetzt anmelden.</p>
              <Link to="/login" className="btn-login" data-testid="to-login-btn">Zur Anmeldung</Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="login-page" data-testid="reset-form-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <div className="login-icon-wrap"><KeyRound className="login-icon" /></div>
              <h1>Neues Passwort</h1>
              <p>Geben Sie Ihr neues Passwort ein</p>
            </div>
            <form onSubmit={handleReset} className="login-form" data-testid="reset-form">
              {error && <div className="form-error"><AlertCircle size={18} /><span>{error}</span></div>}
              <div className="form-group">
                <label>Neues Passwort</label>
                <div className="password-input-wrap">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Neues Passwort" required data-testid="new-password-input" autoComplete="new-password" />
                  <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>
              </div>
              <div className="form-group">
                <label>Passwort bestätigen</label>
                <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Passwort wiederholen" required data-testid="confirm-password-input" autoComplete="new-password" />
              </div>
              <button type="submit" className="btn-login" disabled={loading} data-testid="reset-submit">
                {loading ? <><Loader2 size={18} className="spin" /> Speichern...</> : 'Passwort speichern'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // No token - show request form
  if (requestSent) {
    return (
      <div className="login-page" data-testid="reset-sent">
        <div className="login-container">
          <div className="login-card success-card">
            <CheckCircle size={48} className="success-icon-wrap" style={{ color: '#C8A45C' }} />
            <h1 style={{ color: '#C8A45C' }}>E-Mail gesendet!</h1>
            <p className="success-text">Falls ein Konto mit dieser E-Mail existiert, haben wir einen Link zum Zurücksetzen gesendet. Prüfen Sie Ihren Posteingang.</p>
            <Link to="/login" className="btn-login" data-testid="back-to-login">Zurück zur Anmeldung</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" data-testid="reset-request-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-icon-wrap"><KeyRound className="login-icon" /></div>
            <h1>Passwort vergessen</h1>
            <p>Geben Sie Ihre E-Mail-Adresse ein</p>
          </div>
          <form onSubmit={handleRequest} className="login-form" data-testid="reset-request-form">
            {error && <div className="form-error"><AlertCircle size={18} /><span>{error}</span></div>}
            <div className="form-group">
              <label>E-Mail</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="deine@email.com" required data-testid="reset-email-input" autoComplete="email" />
            </div>
            <button type="submit" className="btn-login" disabled={loading} data-testid="reset-request-submit">
              {loading ? <><Loader2 size={18} className="spin" /> Senden...</> : 'Reset-Link senden'}
            </button>
          </form>
          <div className="login-footer">
            <Link to="/login" className="register-link" data-testid="back-to-login-link"><ArrowLeft size={16} /> Zurück zur Anmeldung</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PasswordReset;
